import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

// Admin control for COD Autopay (UPI Autopay mandate): the on/off switch + per-debit cap, plus a
// "COD Defaulters" table where you can trigger a mandate debit to recover a rider's unsettled COD.
const theme = {
  primary: '#FF6961', primaryDark: '#E6524A', text: '#0F172A', sub: '#64748B', bg: '#F8FAFC', border: '#E2E8F0',
};

const StatusPill = ({ status }) => {
  const map = {
    active: ['#065F46', '#D1FAE5'], initialized: ['#92400E', '#FEF3C7'], paused: ['#92400E', '#FEF3C7'],
    cancelled: ['#991B1B', '#FEE2E2'], failed: ['#991B1B', '#FEE2E2'], none: ['#475569', '#F1F5F9'],
  };
  const [c, bg] = map[status] || map.none;
  return <span style={{ color: c, background: bg, padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>{status}</span>;
};

export default function CodAutopay() {
  const [settings, setSettings] = useState({ enabled: false, maxAmount: 1000, cashfreeConfigured: false });
  const [savingSettings, setSavingSettings] = useState(false);
  const [defaulters, setDefaulters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debiting, setDebiting] = useState(null); // partnerId being debited
  const [msg, setMsg] = useState({ type: '', text: '' });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 5000); };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([adminAPI.codAutopayGetSettings(), adminAPI.codAutopayDefaulters()]);
      setSettings(s.data?.data || { enabled: false, maxAmount: 1000, cashfreeConfigured: false });
      setDefaulters(d.data?.data || []);
    } catch (e) {
      flash('error', 'Failed to load: ' + (e.response?.data?.message || e.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveSettings = async () => {
    setSavingSettings(true); setMsg({ type: '', text: '' });
    try {
      const res = await adminAPI.codAutopayUpdateSettings({ enabled: settings.enabled, maxAmount: Number(settings.maxAmount) || 0 });
      setSettings((p) => ({ ...p, ...(res.data?.data || {}) }));
      flash('success', 'COD Autopay settings saved ✓');
    } catch (e) { flash('error', 'Save failed: ' + (e.response?.data?.message || e.message)); }
    finally { setSavingSettings(false); }
  };

  const debit = async (row) => {
    if (!window.confirm(`Debit ₹${Math.min(row.cashInHand, settings.maxAmount)} from ${row.name} via UPI Autopay to settle their COD?`)) return;
    setDebiting(row.partnerId); setMsg({ type: '', text: '' });
    try {
      const res = await adminAPI.codAutopayDebit(row.partnerId, { reason: 'COD settlement (admin)' });
      const data = res.data?.data || {};
      if (res.data?.success && (data.status === 'SUCCESS' || data.ok)) {
        flash('success', `Debited ₹${data.charged ?? ''} from ${row.name} — ${data.status}`);
      } else {
        flash('error', `${row.name}: ${data.message || data.status || res.data?.message || 'Debit not completed'}`);
      }
      loadAll();
    } catch (e) { flash('error', `${row.name}: ` + (e.response?.data?.message || e.message)); }
    finally { setDebiting(null); }
  };

  const s = {
    card: { background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 12, padding: 24, marginBottom: 20 },
    btn: { background: theme.primary, color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' },
    btnSm: { padding: '6px 12px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', border: 'none', fontSize: 13 },
    input: { padding: '10px 12px', borderRadius: 8, border: `1px solid ${theme.border}`, fontSize: 14, width: 160 },
    th: { textAlign: 'left', padding: '10px 12px', color: theme.sub, fontSize: 12, borderBottom: `2px solid ${theme.border}`, textTransform: 'uppercase' },
    td: { padding: '10px 12px', borderBottom: `1px solid ${theme.border}`, fontSize: 14 },
    hint: { color: theme.sub, fontSize: 13, marginTop: 6 },
  };

  return (
    <div style={{ background: theme.bg, minHeight: '100vh' }}>
      <Navbar />
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: 24 }}>
        <h1 style={{ color: theme.text, fontSize: 24, fontWeight: 800, marginBottom: 4 }}>COD Autopay</h1>
        <p style={{ color: theme.sub, marginBottom: 20 }}>
          UPI Autopay mandate (Cashfree) — riders authorize once; you auto-debit their unsettled COD cash up to the cap.
        </p>

        {msg.text ? (
          <div style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontWeight: 600,
            background: msg.type === 'error' ? '#FEE2E2' : '#D1FAE5', color: msg.type === 'error' ? '#991B1B' : '#065F46' }}>
            {msg.text}
          </div>
        ) : null}

        {/* Settings / on-off */}
        <div style={s.card}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: theme.text, marginBottom: 12 }}>Settings</h2>
          {!settings.cashfreeConfigured && (
            <div style={{ background: '#FEF3C7', color: '#92400E', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
              ⚠️ Cashfree credentials not detected on the server. The Subscriptions product must be enabled on the merchant account before this can run.
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {[{ v: true, label: '✅ Autopay ON' }, { v: false, label: '🚫 Autopay OFF' }].map((opt) => (
              <button key={String(opt.v)} onClick={() => setSettings((p) => ({ ...p, enabled: opt.v }))}
                style={{ flex: '1 1 180px', padding: 14, borderRadius: 10, cursor: 'pointer', fontWeight: 700,
                  border: `2px solid ${settings.enabled === opt.v ? theme.primary : theme.border}`,
                  background: settings.enabled === opt.v ? '#FFF5F5' : '#fff',
                  color: settings.enabled === opt.v ? theme.primaryDark : theme.sub }}>
                {opt.label}
              </button>
            ))}
          </div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: theme.text, marginBottom: 6 }}>
            Per-debit cap (₹)
          </label>
          <input type="number" min="1" step="50" value={settings.maxAmount}
            onChange={(e) => setSettings((p) => ({ ...p, maxAmount: e.target.value === '' ? '' : Number(e.target.value) }))} style={s.input} />
          <div style={s.hint}>Max amount debited per mandate charge. Should match your COD cash-in-hand limit (default ₹1000).</div>
          <div style={{ marginTop: 16 }}>
            <button onClick={saveSettings} disabled={savingSettings} style={s.btn}>{savingSettings ? 'Saving…' : 'Save settings'}</button>
          </div>
          <div style={{ ...s.hint, marginTop: 12 }}>
            ⚠️ A UPI Autopay mandate is <b>revocable by the rider</b> — if a rider cancels while owing, block them (orders + payouts). Turn ON only after the delivery-app mandate screen has shipped and the flow is sandbox-tested.
          </div>
        </div>

        {/* Defaulters */}
        <div style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: theme.text }}>COD Defaulters — riders holding cash</h2>
            <button onClick={loadAll} style={{ ...s.btnSm, background: '#fff', color: theme.sub, border: `1px solid ${theme.border}` }}>↻ Refresh</button>
          </div>
          {loading ? <p style={{ color: theme.sub }}>Loading…</p> : defaulters.length === 0 ? (
            <p style={{ color: theme.sub }}>No riders are currently holding unsettled COD cash. 🎉</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={s.th}>Rider</th><th style={s.th}>Phone</th><th style={s.th}>Cash in hand</th>
                  <th style={s.th}>Orders</th><th style={s.th}>Mandate</th><th style={s.th}>Last debit</th><th style={s.th}></th>
                </tr></thead>
                <tbody>
                  {defaulters.map((r) => (
                    <tr key={r.partnerId}>
                      <td style={s.td}>{r.name || '—'}</td>
                      <td style={s.td}>{r.phone || '—'}</td>
                      <td style={{ ...s.td, fontWeight: 700 }}>₹{r.cashInHand}</td>
                      <td style={s.td}>{r.pendingCount}</td>
                      <td style={s.td}><StatusPill status={r.mandateStatus} /></td>
                      <td style={s.td}>{r.lastDebitAt ? new Date(r.lastDebitAt).toLocaleDateString('en-IN') : '—'}{r.lastDebitStatus ? ` (${r.lastDebitStatus})` : ''}</td>
                      <td style={s.td}>
                        <button
                          onClick={() => debit(r)}
                          disabled={debiting === r.partnerId || r.mandateStatus !== 'active' || !settings.enabled}
                          title={r.mandateStatus !== 'active' ? 'Rider has no active mandate' : (!settings.enabled ? 'Enable autopay first' : '')}
                          style={{ ...s.btnSm, background: (r.mandateStatus === 'active' && settings.enabled) ? theme.primary : '#CBD5E1', color: '#fff' }}>
                          {debiting === r.partnerId ? 'Debiting…' : `Debit ₹${Math.min(r.cashInHand, settings.maxAmount || 0)}`}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
